/**
 * MyAccount — `/account` page (V2.8).
 *
 * Created in Sprint V2.A as the user's "vrai espace" — a dedicated
 * surface for everything that belongs to the user identity itself
 * (as opposed to app preferences, which stay in `/settings`).
 *
 * Initial scaffold lays out 4 sections via the shared `<MonitorTabs>`
 * primitive : Profile / Security / Data / Subscriptions. Each section
 * is currently a thin placeholder that points to where the
 * functionality lives today (mostly inside `Settings.tsx`'s 1 397 LOC).
 *
 * **Migration path** : subsequent commits will move profile / security
 * blocks out of `Settings.tsx` into here, leaving Settings as the
 * pure-preferences page (theme, currency, calculation method,
 * notifications). This is the V2.8 deliverable per
 * `docs/v2/V2_PRIORITIES.md`.
 *
 * Reuses V2 primitives :
 *  - `<DataPageHeader>` for the page header
 *  - `<DataStateCard>` for the unauthenticated state
 *  - `<MonitorTabs>` for the section switcher
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icons } from '../components/Icons';
import { useAuthStore } from '../store/authStore';
import { DataPageHeader } from '../components/ui/DataPageHeader';
import { DataStateCard } from '../components/ui/DataStateCard';
import { MonitorTabs } from '../components/monitor';
import { ACCOUNT_COLORS } from '../config/monitorColors';
import { useFormState } from '../hooks/useFormState';
import { FormField } from '../components/ui/FormField';
import {
  getProfile,
  type UserProfileData,
  changePassword,
  uploadAvatar,
  deleteAvatar,
  updateProfile,
} from '../services/profileApi';
import { pinService } from '../services/pinService';
import { biometricAuth } from '../services/biometricAuth';
import { exportBackup } from '../services/backupService';
import { useToast } from '../components/Toast';
import { PasswordStrengthMeter, usePasswordStrength } from '../components/auth/PasswordStrengthMeter';
import { supabase } from '../lib/supabase/client';

type AccountSection = 'profile' | 'security' | 'notifications' | 'data' | 'subscriptions';

interface MyAccountProps {
  /** Imperative navigation callback (sidebar / App router). */
  onNavigate?: (pageId: string) => void;
  /** Open the PIN setup modal — wired by `App.tsx`'s shell. */
  onOpenPINSetup?: () => void;
  /** Open the biometric setup modal — wired by `App.tsx`'s shell. */
  onOpenBiometricSetup?: () => void;
}

// V2.I.8.b — palette imported from src/config/monitorColors.ts (was a
// local const). Single source of truth for brand colours.

export function MyAccount({ onNavigate, onOpenPINSetup, onOpenBiometricSetup }: MyAccountProps = {}) {
  const { t } = useTranslation();
  const { isAuthenticated, user } = useAuthStore();
  // V2.J.1 : honour ?section=… deep-link (used by the HBAR release
  // email's unsubscribe link, which lands on /account?section=notifications).
  const initialSection = (() => {
    if (typeof window === 'undefined') return 'profile' as const;
    const param = new URLSearchParams(window.location.search).get('section');
    const allowed: AccountSection[] = ['profile', 'security', 'notifications', 'data', 'subscriptions'];
    return (allowed as string[]).includes(param ?? '') ? (param as AccountSection) : 'profile';
  })();
  const [activeSection, setActiveSection] = useState<AccountSection>(initialSection);

  if (!isAuthenticated || !user) {
    return (
      <div style={{ padding: '20px', maxWidth: '900px', margin: '0 auto' }}>
        <DataStateCard
          variant="empty"
          icon={<Icons.Lock size={48} />}
          title={t('account.loginRequired', 'Connexion requise')}
          message={t(
            'account.loginMessage',
            'Connectez-vous pour accéder à votre espace personnel.',
          )}
        />
      </div>
    );
  }

  const tabs: { id: AccountSection; label: string; icon: React.ReactNode }[] = [
    { id: 'profile', label: t('account.tabs.profile', 'Profil'), icon: <Icons.User size={16} /> },
    { id: 'security', label: t('account.tabs.security', 'Sécurité'), icon: <Icons.Shield size={16} /> },
    { id: 'notifications', label: t('account.tabs.notifications', 'Notifications'), icon: <Icons.Bell size={16} /> },
    { id: 'data', label: t('account.tabs.data', 'Données'), icon: <Icons.Database size={16} /> },
    { id: 'subscriptions', label: t('account.tabs.subscriptions', 'Abonnements'), icon: <Icons.CreditCard size={16} /> },
  ];

  return (
    <div style={{ padding: '20px', maxWidth: '1100px', margin: '0 auto' }}>
      <DataPageHeader
        icon={<Icons.User size={28} style={{ color: ACCOUNT_COLORS.primary }} />}
        title={t('account.title', 'Mon Compte')}
        subtitle={t(
          'account.subtitle',
          'Gérez votre identité, votre sécurité, vos données et vos abonnements.',
        )}
      />

      <MonitorTabs
        tabs={tabs}
        activeTab={activeSection}
        onChange={setActiveSection}
        colors={ACCOUNT_COLORS}
        ariaLabel={t('account.tabs.ariaLabel', 'Account sections')}
      />

      {activeSection === 'profile' && <ProfileSection email={user.email} onNavigate={onNavigate} />}
      {activeSection === 'security' && (
        <SecuritySection
          onNavigate={onNavigate}
          onOpenPINSetup={onOpenPINSetup}
          onOpenBiometricSetup={onOpenBiometricSetup}
        />
      )}
      {activeSection === 'notifications' && <NotificationsSection />}
      {activeSection === 'data' && <DataSection onNavigate={onNavigate} />}
      {activeSection === 'subscriptions' && <SubscriptionsSection />}
    </div>
  );
}

// ---- Section components (initial scaffolds) ---------------------------

interface SectionProps {
  onNavigate?: (pageId: string) => void;
}

function ProfileSection({ email, onNavigate }: { email?: string | null; onNavigate?: (pageId: string) => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Avatar upload/delete state — V2.A3.2 brings the actual UI here.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [isDeletingAvatar, setIsDeletingAvatar] = useState(false);
  // Edit mode state for displayName + bio (V2.A3.3).
  // V2.I.13.c : draftDisplayName / draftBio replaced by useFormState
  // for centralised validation + server-side error routing.
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const profileForm = useFormState<{ displayName: string; bio: string }>({
    initial: { displayName: '', bio: '' },
    validate: {
      displayName: (v) => {
        if (v.length > 64) return t('account.profile.displayNameTooLong', 'Maximum 64 caractères');
        return null;
      },
      bio: (v) => {
        if (v.length > 280) return t('account.profile.bioTooLong', 'Maximum 280 caractères');
        return null;
      },
    },
  });

  // Fetch the real profile on mount. Read-only for the rest, but
  // avatar is now editable.
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    getProfile()
      .then((data) => {
        if (!cancelled) {
          setProfile(data);
          setError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load profile');
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const handleAvatarClick = () => {
    if (isUploadingAvatar || isDeletingAvatar) return;
    fileInputRef.current?.click();
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset the file input so re-picking the same file fires onChange.
    e.target.value = '';
    if (!file.type.startsWith('image/')) {
      toast(t('account.profile.avatarTypeError', '❌ Choisissez un fichier image (PNG, JPG, GIF…)'), 'error', 4000);
      return;
    }
    // Hard cap : 5 MB to keep base64 payloads reasonable.
    if (file.size > 5 * 1024 * 1024) {
      toast(t('account.profile.avatarSizeError', '❌ Image trop volumineuse (max 5 Mo)'), 'error', 4000);
      return;
    }
    setIsUploadingAvatar(true);
    try {
      const newUrl = await uploadAvatar(file);
      // Optimistic local update — avoids a second roundtrip to refresh.
      setProfile((prev) => (prev ? { ...prev, avatar_url: newUrl } : prev));
      toast(t('account.profile.avatarUploaded', '✅ Avatar mis à jour'), 'success', 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast(t('account.profile.avatarUploadError', '❌ Erreur upload : {{msg}}', { msg }), 'error', 5000);
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  const handleDeleteAvatar = async () => {
    if (!profile?.avatar_url || isUploadingAvatar || isDeletingAvatar) return;
    setIsDeletingAvatar(true);
    try {
      await deleteAvatar();
      setProfile((prev) => (prev ? { ...prev, avatar_url: null } : prev));
      toast(t('account.profile.avatarDeleted', '✅ Avatar supprimé'), 'success', 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast(t('account.profile.avatarDeleteError', '❌ Erreur suppression : {{msg}}', { msg }), 'error', 5000);
    } finally {
      setIsDeletingAvatar(false);
    }
  };

  const handleStartEdit = () => {
    if (!profile) return;
    profileForm.reset({
      displayName: profile.display_name ?? profile.username ?? '',
      bio: profile.bio ?? '',
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    profileForm.reset();
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    // V2.I.13.c : validate via useFormState (consistent error display).
    if (!profileForm.validateAll()) return;
    setIsSaving(true);
    try {
      // Only send fields that actually changed — server-side validators
      // typically reject empty strings differently from "unchanged".
      const patch: { display_name?: string; bio?: string } = {};
      const trimmedName = profileForm.values.displayName.trim();
      const trimmedBio = profileForm.values.bio.trim();
      if (trimmedName !== (profile.display_name ?? '')) {
        patch.display_name = trimmedName;
      }
      if (trimmedBio !== (profile.bio ?? '')) {
        patch.bio = trimmedBio;
      }
      if (Object.keys(patch).length === 0) {
        // No-op : just leave edit mode, don't hit the API.
        setIsEditing(false);
        return;
      }
      const updated = await updateProfile(patch);
      setProfile(updated);
      setIsEditing(false);
      toast(t('account.profile.updated', '✅ Profil mis à jour'), 'success', 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      // V2.I.13.c : route server-side errors through the form so they
      // appear next to the relevant field. Best-effort routing — the
      // server message is generic so we attach it to displayName.
      profileForm.setError('displayName', msg);
      toast(t('account.profile.updateError', '❌ Erreur : {{msg}}', { msg }), 'error', 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const displayName = profile?.display_name ?? profile?.username ?? null;
  const verified = profile?.email_verified ?? false;
  const memberSince = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <SectionCard
      title={t('account.profile.title', 'Profil')}
      description={t(
        'account.profile.description',
        "Votre identité publique : avatar, nom d'affichage, e-mail. L'édition reste pour l'instant dans Settings.",
      )}
    >
      {isLoading ? (
        <p style={{ color: 'var(--muted)', fontSize: '13px' }} aria-live="polite">
          {t('account.profile.loading', 'Chargement du profil…')}
        </p>
      ) : error ? (
        <p style={{ color: '#F87171', fontSize: '13px' }}>
          {t('account.profile.error', 'Erreur de chargement :')} {error}
        </p>
      ) : (
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {/* Avatar (clickable — opens file picker) */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <button
              type="button"
              onClick={handleAvatarClick}
              disabled={isUploadingAvatar || isDeletingAvatar}
              aria-label={t('account.profile.avatarUpload', "Modifier l'avatar")}
              style={{
                width: '96px',
                height: '96px',
                borderRadius: '50%',
                background: profile?.avatar_url
                  ? `url(${profile.avatar_url}) center/cover`
                  : 'linear-gradient(135deg, #6366F1, #EC4899)',
                border: '3px solid rgba(255, 255, 255, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '36px',
                fontWeight: 700,
                cursor: isUploadingAvatar || isDeletingAvatar ? 'wait' : 'pointer',
                position: 'relative',
                overflow: 'hidden',
                padding: 0,
              }}
            >
              {isUploadingAvatar ? (
                <Icons.Loader size={28} style={{ animation: 'spin 1s linear infinite' }} />
              ) : !profile?.avatar_url ? (
                (displayName ?? email ?? 'U')[0]?.toUpperCase()
              ) : null}
              {/* Hover-style camera affordance always visible at the bottom */}
              {!isUploadingAvatar && (
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    background: 'rgba(0, 0, 0, 0.55)',
                    padding: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icons.Camera size={14} />
                </span>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              hidden
              aria-hidden
              tabIndex={-1}
            />
            {profile?.avatar_url && (
              <button
                type="button"
                onClick={handleDeleteAvatar}
                disabled={isDeletingAvatar || isUploadingAvatar}
                style={{
                  padding: '4px 10px',
                  background: 'rgba(248, 113, 113, 0.12)',
                  color: '#F87171',
                  border: '1px solid rgba(248, 113, 113, 0.3)',
                  borderRadius: '8px',
                  fontSize: '11px',
                  fontWeight: 600,
                  cursor: isDeletingAvatar || isUploadingAvatar ? 'wait' : 'pointer',
                  opacity: isDeletingAvatar || isUploadingAvatar ? 0.6 : 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                {isDeletingAvatar ? (
                  <Icons.Loader size={11} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <Icons.Trash2 size={11} />
                )}
                {t('account.profile.avatarRemove', 'Supprimer')}
              </button>
            )}
          </div>
          {/* Info */}
          <div style={{ flex: 1, minWidth: '300px' }}>
            {isEditing ? (
              <form onSubmit={handleSaveEdit} aria-label={t('account.profile.editForm', 'Modifier mon profil')}>
                {/* V2.I.13.c : <FormField> wraps inputs for uniform label
                    + error display (aria-invalid + aria-describedby + red
                    border on error). char counts kept as inline hints. */}
                <FormField
                  label={t('account.profile.displayName', "Nom d'affichage")}
                  hint={`${profileForm.values.displayName.length} / 64`}
                  error={profileForm.errorIfTouched('displayName')}
                >
                  <input
                    type="text"
                    value={profileForm.values.displayName}
                    onChange={(e) => profileForm.setValue('displayName', e.target.value)}
                    onBlur={() => profileForm.setTouched('displayName')}
                    maxLength={64}
                    disabled={isSaving}
                    style={fieldInputStyle}
                    autoFocus
                  />
                </FormField>
                <FormField
                  label={t('account.profile.bio', 'Bio')}
                  hint={`${profileForm.values.bio.length} / 280`}
                  error={profileForm.errorIfTouched('bio')}
                >
                  <textarea
                    value={profileForm.values.bio}
                    onChange={(e) => profileForm.setValue('bio', e.target.value)}
                    onBlur={() => profileForm.setTouched('bio')}
                    maxLength={280}
                    rows={3}
                    disabled={isSaving}
                    placeholder={t('account.profile.bioPlaceholder', 'Quelques mots sur vous (280 caractères max)')}
                    style={{ ...fieldInputStyle, resize: 'vertical', fontFamily: 'inherit' }}
                  />
                </FormField>
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                  <button
                    type="submit"
                    disabled={isSaving}
                    style={{
                      padding: '8px 14px',
                      background: isSaving ? 'rgba(255, 255, 255, 0.05)' : 'linear-gradient(135deg, #4F46E5, #6366F1)',
                      color: isSaving ? 'var(--muted)' : 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: isSaving ? 'wait' : 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                    }}
                  >
                    {isSaving ? (
                      <>
                        <Icons.Loader size={12} style={{ animation: 'spin 1s linear infinite' }} />
                        {t('account.profile.saving', 'Enregistrement…')}
                      </>
                    ) : (
                      <>
                        <Icons.Check size={12} />
                        {t('account.profile.save', 'Enregistrer')}
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    style={{
                      padding: '8px 14px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: 'var(--text)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '8px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: isSaving ? 'not-allowed' : 'pointer',
                      opacity: isSaving ? 0.5 : 1,
                    }}
                  >
                    {t('account.profile.cancel', 'Annuler')}
                  </button>
                </div>
              </form>
            ) : (
              <dl style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '12px 16px', margin: 0 }}>
                <dt style={dtStyle}>{t('account.profile.displayName', "Nom d'affichage")}</dt>
                <dd style={ddStyle}>{displayName ?? <span style={{ color: 'var(--muted)' }}>—</span>}</dd>

                <dt style={dtStyle}>{t('account.profile.email', 'E-mail')}</dt>
                <dd style={ddStyle}>
                  {/* email + badge on a single flex row that wraps on
                      narrow viewports — avoids the badge getting orphaned
                      behind a long email + line-break. */}
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <code style={{ fontSize: '13px', wordBreak: 'break-all' }}>{email ?? '—'}</code>
                    {verified ? (
                      <span style={badgeOkStyle} title={t('account.profile.emailVerified', 'E-mail vérifié')}>
                        <Icons.Check size={11} /> {t('account.profile.verified', 'vérifié')}
                      </span>
                    ) : (
                      <span style={badgeWarnStyle} title={t('account.profile.emailNotVerified', 'E-mail non vérifié')}>
                        {t('account.profile.notVerified', 'non vérifié')}
                      </span>
                    )}
                  </div>
                </dd>

                {profile?.bio && (
                  <>
                    <dt style={dtStyle}>{t('account.profile.bio', 'Bio')}</dt>
                    <dd style={{ ...ddStyle, whiteSpace: 'pre-wrap' as const }}>{profile.bio}</dd>
                  </>
                )}

                <dt style={dtStyle}>{t('account.profile.subscription', 'Plan')}</dt>
                <dd style={ddStyle}>
                  {profile?.subscription_tier
                    ? profile.subscription_tier.charAt(0).toUpperCase() + profile.subscription_tier.slice(1)
                    : 'Free'}
                </dd>

                {memberSince && (
                  <>
                    <dt style={dtStyle}>{t('account.profile.memberSince', 'Membre depuis')}</dt>
                    <dd style={ddStyle}>{memberSince}</dd>
                  </>
                )}
              </dl>
            )}
          </div>
        </div>
      )}
      {!isEditing && profile && (
        <button
          type="button"
          onClick={handleStartEdit}
          style={{
            marginTop: '16px',
            padding: '8px 14px',
            background: 'rgba(99, 102, 241, 0.15)',
            color: '#A5B4FC',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '10px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Icons.Edit size={14} />
          {t('account.profile.editCta', 'Modifier mon profil')}
        </button>
      )}
      {!isEditing && (
        <p style={{ fontSize: '11px', color: 'var(--muted)', margin: '12px 0 0 0' }}>
          {t(
            'account.profile.advancedNote',
            "Les paramètres avancés (langue, devise, préférences UI) restent dans Settings.",
          )}{' '}
          <button
            type="button"
            onClick={() => onNavigate?.('settings')}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#A5B4FC',
              cursor: 'pointer',
              fontSize: '11px',
              padding: 0,
              textDecoration: 'underline',
            }}
          >
            {t('account.profile.openSettings', 'Ouvrir Settings')}
          </button>
        </p>
      )}
    </SectionCard>
  );
}

function SecuritySection({
  onNavigate,
  onOpenPINSetup,
  onOpenBiometricSetup,
}: SectionProps & { onOpenPINSetup?: () => void; onOpenBiometricSetup?: () => void }) {
  const { t } = useTranslation();
  // V2.A3.x — security flags are state instead of direct reads, so
  // the badge updates after the user finishes a PIN / biometric setup
  // (the modals live in App.tsx and don't fire a close callback to us).
  // The status is re-read in 3 cases :
  //   1. Initial mount (`useState` lazy initializer).
  //   2. Window focus / visibility change (catches tab-out / tab-back).
  //   3. After a delay following a Setup button click (catches the
  //      typical modal-complete flow within 10s).
  const [refreshKey, setRefreshKey] = useState(0);
  const pinEnabled = useMemo(() => pinService.isPINEnabled(), [refreshKey]);
  const biometricEnabled = useMemo(() => biometricAuth.isBiometricEnabled(), [refreshKey]);
  // Capability check is invariant per page lifetime — memoize once.
  const biometricSupported = useMemo(() => biometricAuth.isBiometricSupported(), []);
  // 2FA TOTP n'est pas encore wiré côté Supabase Auth dans cette app —
  // affiché comme "à venir" pour clarifier l'état au user.
  const twoFactorEnabled = false;

  // Track active setTimeout ids so we can clear them on unmount —
  // otherwise React warns about setState on an unmounted component
  // when the user leaves the Security tab during a PIN/biometric flow.
  const refreshTimersRef = useRef<number[]>([]);

  useEffect(() => {
    const refresh = () => setRefreshKey((k) => k + 1);
    // Filter visibilitychange to only re-read when document becomes
    // visible — Firefox / Safari fire this on going-hidden too.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', handleVisibility);
      // Clear any pending setup refreshes scheduled by triggerSetupRefresh.
      refreshTimersRef.current.forEach((id) => window.clearTimeout(id));
      refreshTimersRef.current = [];
    };
  }, []);

  // Defensive multi-stage refresh after Setup button click — the modal
  // lives outside this component so we can't observe its close. We
  // re-read at 0.8s / 3s / 10s ; the first hit that sees the new value
  // updates the badge. Timer ids stored in a ref so unmount cleanup
  // can clear them.
  const triggerSetupRefresh = () => {
    [800, 3000, 10000].forEach((delay) => {
      const id = window.setTimeout(() => setRefreshKey((k) => k + 1), delay);
      refreshTimersRef.current.push(id);
    });
  };

  const handleOpenPINSetup = onOpenPINSetup
    ? () => {
        onOpenPINSetup();
        triggerSetupRefresh();
      }
    : undefined;

  const handleOpenBiometricSetup = onOpenBiometricSetup
    ? () => {
        onOpenBiometricSetup();
        triggerSetupRefresh();
      }
    : undefined;

  return (
    <SectionCard
      title={t('account.security.title', 'Sécurité')}
      description={t(
        'account.security.description',
        "Vue d'ensemble de votre sécurité. La configuration détaillée vit dans Settings pour l'instant.",
      )}
    >
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 20px 0', display: 'grid', gap: '12px' }}>
        <SecurityStatusRow
          label={t('account.security.password', 'Mot de passe')}
          status="enabled"
          detail={t('account.security.passwordDetail', 'Géré par Supabase Auth')}
        />
        <SecurityStatusRow
          label={t('account.security.pin', 'Code PIN')}
          status={pinEnabled ? 'enabled' : 'disabled'}
          detail={
            pinEnabled
              ? t('account.security.pinEnabled', 'Activé — verrouille l\'app au démarrage')
              : t('account.security.pinDisabled', 'Non configuré')
          }
          action={
            handleOpenPINSetup && (
              <SecurityActionButton
                onClick={handleOpenPINSetup}
                label={pinEnabled ? t('account.security.pinChange', 'Modifier') : t('account.security.pinSetup', 'Configurer')}
                ariaLabel={t('account.security.pinAria', 'Configurer le code PIN')}
              />
            )
          }
        />
        <SecurityStatusRow
          label={t('account.security.biometric', 'Biométrie')}
          status={biometricEnabled ? 'enabled' : biometricSupported ? 'disabled' : 'unsupported'}
          detail={
            !biometricSupported
              ? t('account.security.biometricUnsupported', 'Non supporté sur ce navigateur')
              : biometricEnabled
                ? t('account.security.biometricEnabled', 'Activée — Face ID / Touch ID disponible')
                : t('account.security.biometricDisabled', 'Disponible mais non activée')
          }
          action={
            biometricSupported && handleOpenBiometricSetup && (
              <SecurityActionButton
                onClick={handleOpenBiometricSetup}
                label={biometricEnabled ? t('account.security.biometricManage', 'Gérer') : t('account.security.biometricSetup', 'Configurer')}
                ariaLabel={t('account.security.biometricAria', 'Configurer la biométrie')}
              />
            )
          }
        />
        <SecurityStatusRow
          label={t('account.security.twoFactor', 'Authentification 2FA')}
          status={twoFactorEnabled ? 'enabled' : 'planned'}
          detail={t('account.security.twoFactorPlanned', 'TOTP arrive dans le sprint V2.7')}
        />
      </ul>
      <PasswordChangeForm />

      <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.06)' }}>
        <p style={{ fontSize: '12px', color: 'var(--muted)', margin: '0 0 12px 0' }}>
          {t('account.security.otherCta', 'Pour des préférences app (theme, devise, notifications) — voir Settings.')}
        </p>
        <CallToActionButton
          onClick={() => onNavigate?.('settings')}
          label={t('account.security.openSettings', 'Ouvrir les paramètres avancés')}
        />
      </div>
    </SectionCard>
  );
}

/**
 * PasswordChangeForm — embedded directly in the Security section.
 *
 * Calls `changePassword(currentPassword, newPassword)` from profileApi.
 * Reuses the auth surface's `<PasswordStrengthMeter>` + `usePasswordStrength`
 * hook so the validation logic stays consistent with Register / Reset flows.
 *
 * Three required fields with inline validation :
 *  - Current password (required, otherwise the API rejects)
 *  - New password (zxcvbn score ≥ 3 — same as Register)
 *  - Confirm new password (must match)
 *
 * On success : toast + reset all 3 fields. On error : inline error
 * message under the form (also a toast for redundancy).
 */
function PasswordChangeForm() {
  const { t } = useTranslation();
  const toast = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isValid: isStrongEnough } = usePasswordStrength(newPassword, 3);
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword;
  const canSubmit = currentPassword.length > 0 && isStrongEnough && passwordsMatch && !isSubmitting;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError(null);
    setIsSubmitting(true);
    try {
      await changePassword(currentPassword, newPassword);
      toast(t('account.security.passwordChanged', '✅ Mot de passe modifié avec succès'), 'success', 4000);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
      toast(t('account.security.passwordChangeError', '❌ Erreur : {{msg}}', { msg }), 'error', 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} aria-label={t('account.security.passwordForm', 'Changer mon mot de passe')}>
      <h3 style={{ fontSize: '14px', fontWeight: 600, margin: '0 0 12px 0' }}>
        {t('account.security.passwordFormTitle', 'Changer mon mot de passe')}
      </h3>
      <div style={{ display: 'grid', gap: '12px' }}>
        <label style={fieldLabelStyle}>
          <span>{t('account.security.currentPassword', 'Mot de passe actuel')}</span>
          <input
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            autoComplete="current-password"
            required
            disabled={isSubmitting}
            style={fieldInputStyle}
          />
        </label>
        <label style={fieldLabelStyle}>
          <span>{t('account.security.newPassword', 'Nouveau mot de passe')}</span>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
            disabled={isSubmitting}
            style={fieldInputStyle}
          />
          {newPassword.length > 0 && <PasswordStrengthMeter password={newPassword} minScore={3} />}
        </label>
        <label style={fieldLabelStyle}>
          <span>{t('account.security.confirmPassword', 'Confirmer le nouveau mot de passe')}</span>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            autoComplete="new-password"
            required
            disabled={isSubmitting}
            aria-invalid={confirmPassword.length > 0 && !passwordsMatch}
            style={{
              ...fieldInputStyle,
              borderColor: confirmPassword.length > 0 && !passwordsMatch ? '#F87171' : 'rgba(255, 255, 255, 0.15)',
            }}
          />
          {confirmPassword.length > 0 && !passwordsMatch && (
            <span style={{ fontSize: '12px', color: '#F87171', marginTop: '4px' }}>
              {t('account.security.passwordMismatch', 'Les mots de passe ne correspondent pas.')}
            </span>
          )}
        </label>
      </div>
      {error && (
        <p role="alert" style={{ fontSize: '13px', color: '#F87171', marginTop: '12px' }}>
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={!canSubmit}
        style={{
          marginTop: '16px',
          padding: '10px 16px',
          background: canSubmit ? 'linear-gradient(135deg, #4F46E5, #6366F1)' : 'rgba(255, 255, 255, 0.05)',
          color: canSubmit ? 'white' : 'var(--muted)',
          border: 'none',
          borderRadius: '10px',
          fontSize: '13px',
          fontWeight: 600,
          cursor: canSubmit ? 'pointer' : 'not-allowed',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        {isSubmitting ? (
          <>
            <Icons.Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
            {t('account.security.changing', 'Modification…')}
          </>
        ) : (
          <>
            <Icons.Key size={14} />
            {t('account.security.changeNow', 'Modifier mon mot de passe')}
          </>
        )}
      </button>
    </form>
  );
}

function SecurityStatusRow({
  label,
  status,
  detail,
  action,
}: {
  label: string;
  status: 'enabled' | 'disabled' | 'unsupported' | 'planned';
  detail: string;
  /** Optional CTA rendered to the right of the status badge — used for
   *  Setup / Manage buttons on PIN + biometric rows. */
  action?: React.ReactNode;
}) {
  const palette = {
    enabled: { bg: 'rgba(16, 185, 129, 0.12)', fg: '#34D399', border: 'rgba(16, 185, 129, 0.25)', icon: <Icons.Check size={11} /> },
    disabled: { bg: 'rgba(245, 158, 11, 0.12)', fg: '#FBBF24', border: 'rgba(245, 158, 11, 0.25)', icon: null },
    unsupported: { bg: 'rgba(255, 255, 255, 0.05)', fg: 'var(--muted)', border: 'rgba(255, 255, 255, 0.08)', icon: null },
    planned: { bg: 'rgba(99, 102, 241, 0.12)', fg: '#A5B4FC', border: 'rgba(99, 102, 241, 0.25)', icon: <Icons.Clock size={11} /> },
  }[status];
  const statusLabel = {
    enabled: 'Activé',
    disabled: 'Désactivé',
    unsupported: 'Indisponible',
    planned: 'À venir',
  }[status];
  return (
    <li
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '12px 16px',
        background: 'rgba(0, 0, 0, 0.15)',
        border: '1px solid rgba(255, 255, 255, 0.06)',
        borderRadius: '10px',
        gap: '16px',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: 1, minWidth: '200px' }}>
        <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '2px' }}>{label}</div>
        <div style={{ fontSize: '12px', color: 'var(--muted)' }}>{detail}</div>
      </div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            background: palette.bg,
            color: palette.fg,
            border: `1px solid ${palette.border}`,
            borderRadius: '999px',
            fontSize: '11px',
            fontWeight: 600,
          }}
        >
          {palette.icon}
          {statusLabel}
        </span>
        {action}
      </div>
    </li>
  );
}

function SecurityActionButton({
  onClick,
  label,
  ariaLabel,
}: {
  onClick: () => void;
  label: string;
  ariaLabel: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        padding: '5px 12px',
        background: 'rgba(99, 102, 241, 0.15)',
        color: '#A5B4FC',
        border: '1px solid rgba(99, 102, 241, 0.3)',
        borderRadius: '8px',
        fontSize: '11px',
        fontWeight: 600,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function DataSection({ onNavigate }: SectionProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [isExporting, setIsExporting] = useState(false);

  // Real export action — calls backupService directly. Triggers a JSON
  // download of the user's full portfolio + alerts + cache.
  const handleExport = () => {
    setIsExporting(true);
    try {
      const result = exportBackup();
      if (result.success) {
        toast(
          t('account.data.exportSuccess', '✅ Export réussi : {{summary}}', { summary: result.summary }),
          'success',
          4000,
        );
      } else {
        toast(
          t('account.data.exportError', "❌ Erreur d'export : {{error}}", { error: result.error ?? 'unknown' }),
          'error',
          5000,
        );
      }
    } catch (err) {
      toast(
        t('account.data.exportError', "❌ Erreur d'export : {{error}}", {
          error: err instanceof Error ? err.message : 'unknown',
        }),
        'error',
        5000,
      );
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <SectionCard
      title={t('account.data.title', 'Vos données')}
      description={t(
        'account.data.description',
        "Exportez la totalité de vos portfolios (JSON), ou retournez à la page Portfolio pour des exports CSV/tax. La suppression de compte arrivera au sprint V2.A3.",
      )}
    >
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={handleExport}
          disabled={isExporting}
          style={{
            padding: '10px 16px',
            background: 'linear-gradient(135deg, #4F46E5, #6366F1)',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: isExporting ? 'wait' : 'pointer',
            opacity: isExporting ? 0.6 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          <Icons.Download size={14} />
          {isExporting
            ? t('account.data.exporting', 'Export en cours…')
            : t('account.data.exportNow', 'Exporter (JSON)')}
        </button>
        <button
          type="button"
          onClick={() => onNavigate?.('portfolio')}
          disabled={!onNavigate}
          style={{
            padding: '10px 16px',
            background: 'rgba(99, 102, 241, 0.15)',
            color: '#A5B4FC',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '10px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: onNavigate ? 'pointer' : 'not-allowed',
            opacity: onNavigate ? 1 : 0.5,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          {t('account.data.openPortfolio', 'Ouvrir Portfolio (CSV / tax)')}
          <Icons.ArrowRight size={14} />
        </button>
      </div>
    </SectionCard>
  );
}

/**
 * NotificationsSection — V2.J.1
 *
 * Per-topic notification opt-out. Reads + writes
 * user_preferences.notification_settings JSONB. First topic on the
 * roster is `hbar_release` (HBAR treasury releases — see
 * /api/cron/check-hbar-release).
 *
 * Pattern is generic so future topics (e.g. portfolio_daily_summary,
 * tax_year_close) plug in by extending the NOTIFICATION_TOPICS array.
 */
type NotifKey = 'hbar_release';
interface NotifTopic {
  key: NotifKey;
  icon: React.ReactNode;
  defaultEnabled: boolean;
  title: string;
  description: string;
}

function NotificationsSection() {
  const { t } = useTranslation();
  const toast = useToast();
  const { user } = useAuthStore();
  const [settings, setSettings] = useState<Record<string, unknown> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const topics: NotifTopic[] = [
    {
      key: 'hbar_release',
      icon: <span aria-hidden style={{ fontSize: '18px' }}>ℏ</span>,
      defaultEnabled: true,
      title: t('account.notifications.hbarRelease.title', 'Libérations HBAR'),
      description: t(
        'account.notifications.hbarRelease.description',
        "Recevez un email lorsqu'une libération de tokens depuis la trésorerie Hedera dépasse le seuil configuré. Calibré pour la libération T2 2026 ~3,97 Md attendue en ~10 sous-événements.",
      ),
    },
  ];

  // Load preferences on mount.
  useEffect(() => {
    let cancelled = false;
    if (!user?.id) return;
    setIsLoading(true);
    // V2.J.1 : `user_preferences` isn't part of the generated Database
    // type yet — cast through `any` for read + upsert below. RLS gate
    // still enforced server-side.
    (supabase as any)
      .from('user_preferences')
      .select('notification_settings')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data, error }: { data: { notification_settings: Record<string, unknown> } | null; error: { message: string } | null }) => {
        if (cancelled) return;
        if (error) {
          console.error('[NotificationsSection] load error', error.message);
          setSettings({});
        } else {
          setSettings((data?.notification_settings as Record<string, unknown>) || {});
        }
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const isEnabled = (topic: NotifTopic): boolean => {
    if (!settings) return topic.defaultEnabled;
    const v = settings[topic.key];
    if (v === undefined) return topic.defaultEnabled;
    return v !== false;
  };

  const toggle = async (topic: NotifTopic) => {
    if (!user?.id || !settings) return;
    const nextEnabled = !isEnabled(topic);
    const nextSettings = { ...settings, [topic.key]: nextEnabled };
    setSavingKey(topic.key);
    setSettings(nextSettings); // optimistic
    try {
      // Upsert so the row is created if the user has none yet (which
      // can happen for accounts that pre-date the handle_new_user
      // trigger).
      const { error } = await (supabase as any)
        .from('user_preferences')
        .upsert(
          { user_id: user.id, notification_settings: nextSettings },
          { onConflict: 'user_id' },
        );
      if (error) throw error;
      toast(
        nextEnabled
          ? t('account.notifications.enabled', '✅ Notification activée')
          : t('account.notifications.disabled', '✅ Notification désactivée'),
        'success',
        3000,
      );
    } catch (err) {
      // Revert on failure.
      setSettings(settings);
      toast(
        t('account.notifications.saveError', '❌ Échec de la sauvegarde : {{msg}}', {
          msg: err instanceof Error ? err.message : 'unknown',
        }),
        'error',
        4000,
      );
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <SectionCard
      title={t('account.notifications.title', 'Notifications')}
      description={t(
        'account.notifications.description',
        'Choisissez quelles alertes vous voulez recevoir par email. Chaque option est indépendante.',
      )}
    >
      {isLoading ? (
        <p style={{ color: 'var(--muted)', fontSize: '13px' }} aria-live="polite">
          {t('account.notifications.loading', 'Chargement de vos préférences…')}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {topics.map((topic) => {
            const enabled = isEnabled(topic);
            const saving = savingKey === topic.key;
            return (
              <div
                key={topic.key}
                style={{
                  display: 'flex',
                  gap: '14px',
                  alignItems: 'flex-start',
                  padding: '14px 16px',
                  background: 'rgba(255, 255, 255, 0.02)',
                  border: '1px solid var(--border)',
                  borderRadius: '12px',
                }}
              >
                <div
                  style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '10px',
                    background:
                      'linear-gradient(135deg, rgba(0, 194, 255, 0.2), rgba(0, 0, 0, 0.4))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {topic.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>
                    {topic.title}
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px', lineHeight: 1.5 }}>
                    {topic.description}
                  </div>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={enabled}
                  aria-label={topic.title}
                  disabled={saving}
                  onClick={() => toggle(topic)}
                  style={{
                    width: '44px',
                    height: '24px',
                    background: enabled ? 'linear-gradient(135deg, #4F46E5, #6366F1)' : 'rgba(255,255,255,0.1)',
                    border: 'none',
                    borderRadius: '999px',
                    position: 'relative',
                    cursor: saving ? 'wait' : 'pointer',
                    opacity: saving ? 0.6 : 1,
                    transition: 'background 0.2s',
                    flexShrink: 0,
                    padding: 0,
                  }}
                >
                  <span
                    aria-hidden
                    style={{
                      position: 'absolute',
                      top: '3px',
                      left: enabled ? '23px' : '3px',
                      width: '18px',
                      height: '18px',
                      background: 'white',
                      borderRadius: '50%',
                      transition: 'left 0.2s',
                    }}
                  />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}

function SubscriptionsSection() {
  const { t } = useTranslation();
  return (
    <SectionCard
      title={t('account.subscriptions.title', 'Abonnements')}
      description={t(
        'account.subscriptions.description',
        "Vous utilisez actuellement le plan gratuit. Les plans payants (Pro / Enterprise) arriveront en Phase 6 de la roadmap.",
      )}
    >
      <p style={{ color: 'var(--muted)', fontSize: '13px', margin: 0 }}>
        {t('account.subscriptions.placeholder', 'Cette section sera complétée lors du sprint billing.')}
      </p>
    </SectionCard>
  );
}

// ---- Tiny shared shells -----------------------------------------------

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section
      aria-label={title}
      style={{
        background: 'rgba(0, 0, 0, 0.2)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '14px',
        padding: '20px',
      }}
    >
      <h2 style={{ fontSize: '18px', fontWeight: 600, marginTop: 0, marginBottom: '8px' }}>{title}</h2>
      <p style={{ color: 'var(--muted)', fontSize: '13px', marginTop: 0, marginBottom: '20px', lineHeight: 1.5 }}>
        {description}
      </p>
      {children}
    </section>
  );
}

function CallToActionButton({ onClick, label }: { onClick?: () => void; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      style={{
        marginTop: '20px',
        padding: '10px 16px',
        background: 'rgba(99, 102, 241, 0.15)',
        color: '#A5B4FC',
        border: '1px solid rgba(99, 102, 241, 0.3)',
        borderRadius: '10px',
        fontSize: '13px',
        fontWeight: 600,
        cursor: onClick ? 'pointer' : 'not-allowed',
        opacity: onClick ? 1 : 0.5,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      {label}
      <Icons.ArrowRight size={14} />
    </button>
  );
}

const dtStyle: React.CSSProperties = {
  fontSize: '12px',
  color: 'var(--muted)',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  fontWeight: 600,
  margin: 0,
};

const ddStyle: React.CSSProperties = {
  fontSize: '14px',
  color: 'var(--text)',
  margin: 0,
};

const badgeOkStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '2px 8px',
  background: 'rgba(16, 185, 129, 0.12)',
  color: '#34D399',
  border: '1px solid rgba(16, 185, 129, 0.25)',
  borderRadius: '999px',
  fontSize: '11px',
  fontWeight: 600,
};

const badgeWarnStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  padding: '2px 8px',
  background: 'rgba(245, 158, 11, 0.12)',
  color: '#FBBF24',
  border: '1px solid rgba(245, 158, 11, 0.25)',
  borderRadius: '999px',
  fontSize: '11px',
  fontWeight: 600,
};

const fieldLabelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  fontSize: '13px',
  color: 'var(--text)',
  fontWeight: 500,
};

const fieldInputStyle: React.CSSProperties = {
  padding: '10px 12px',
  background: 'rgba(0, 0, 0, 0.3)',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  borderRadius: '10px',
  color: 'var(--text)',
  fontSize: '13px',
  outline: 'none',
};

export default MyAccount;
